/**
 * Project Storage Utility
 *
 * Manages project metadata in localStorage for recent projects list.
 */

export interface StoredProject {
  id: string;
  name: string;
  description?: string;
  templateId?: string;
  createdAt: string;
  lastAccessedAt: string;
}

const STORAGE_KEY = 'vibe-coder-projects';
const MAX_RECENT_PROJECTS = 10;

/**
 * Get all stored projects, sorted by last accessed
 */
export function getStoredProjects(): StoredProject[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];

    const projects: StoredProject[] = JSON.parse(data);
    return projects.sort(
      (a, b) => new Date(b.lastAccessedAt).getTime() - new Date(a.lastAccessedAt).getTime()
    );
  } catch {
    return [];
  }
}

/**
 * Save or update a project in storage
 */
export function saveProject(project: Omit<StoredProject, 'lastAccessedAt'> & { lastAccessedAt?: string }): void {
  try {
    const projects = getStoredProjects();
    const existingIndex = projects.findIndex((p) => p.id === project.id);

    const updatedProject: StoredProject = {
      ...project,
      lastAccessedAt: project.lastAccessedAt || new Date().toISOString(),
    };

    if (existingIndex >= 0) {
      projects[existingIndex] = updatedProject;
    } else {
      projects.unshift(updatedProject);
    }

    // Keep only the most recent projects
    const trimmedProjects = projects.slice(0, MAX_RECENT_PROJECTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedProjects));
  } catch {
  }
}

/**
 * Update the last accessed time for a project
 */
export function touchProject(projectId: string): void {
  try {
    const projects = getStoredProjects();
    const project = projects.find((p) => p.id === projectId);

    if (project) {
      saveProject({
        ...project,
        lastAccessedAt: new Date().toISOString(),
      });
    }
  } catch {
  }
}

/**
 * Delete a project from storage
 */
export function deleteProject(projectId: string): void {
  try {
    const projects = getStoredProjects();
    const filtered = projects.filter((p) => p.id !== projectId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch {
  }
}

/**
 * Generate a new project ID
 */
export function generateProjectId(): string {
  return crypto.randomUUID();
}

/**
 * Create a new project with default values
 */
export function createProject(name?: string, templateId?: string): StoredProject {
  const project: StoredProject = {
    id: generateProjectId(),
    name: name || 'Untitled Project',
    templateId: templateId || 'react-cfw-fullstack-app-v1',
    createdAt: new Date().toISOString(),
    lastAccessedAt: new Date().toISOString(),
  };

  saveProject(project);
  return project;
}
